
/// <reference types="google.maps" />
import { waitForGoogleMaps } from './googleMaps'

/**
 * Distance calculation result
 */
export interface DistanceResult {
  distance: number // Distance in miles
  duration?: number // Duration in minutes (optional)
  status: google.maps.DistanceMatrixStatus
}

/**
 * Calculate distance between two addresses using Google Distance Matrix API.
 * Matches Angular: directly creates DistanceMatrixService if google.maps is available,
 * falls back to waitForGoogleMaps() only if not already loaded.
 */
export async function calculateDistance(
  origin: string,
  destination: string,
  travelMode?: google.maps.TravelMode
): Promise<DistanceResult> {
  if (!window.google?.maps) {
    await waitForGoogleMaps()
  }

  if (!window.google?.maps) {
    throw new Error('Google Maps API not loaded')
  }

  if (!origin || !destination) {
    throw new Error('Origin and destination addresses are required')
  }

  return new Promise((resolve, reject) => {
    const service = new google.maps.DistanceMatrixService()
    const resolvedTravelMode = travelMode ?? google.maps.TravelMode.DRIVING

    service.getDistanceMatrix(
      {
        origins: [origin],
        destinations: [destination],
        travelMode: resolvedTravelMode,
        unitSystem: google.maps.UnitSystem.IMPERIAL,
      },
      (response, status) => {
        if (
          status === google.maps.DistanceMatrixStatus.OK &&
          response?.rows?.[0]?.elements?.[0]?.distance?.value
        ) {
          const element = response.rows[0].elements[0]
          const distanceInMiles = element.distance!.value / 1609.34

          resolve({
            distance: distanceInMiles,
            duration: (element.duration?.value || 0) / 60,
            status: google.maps.DistanceMatrixStatus.OK,
          })
        } else {
          const element = response?.rows?.[0]?.elements?.[0]
          reject(
            new Error(
              `Distance calculation failed: status=${status}, elementStatus=${element?.status || 'N/A'}`
            )
          )
        }
      }
    )
  })
}

/**
 * Calculate distance from property address to employer address
 * Used for employment verification in resident applications
 * @param propertyAddress - Full property address string
 * @param employerAddress - Full employer address string
 * @returns Promise with distance in miles
 */
export async function calculatePropertyToEmployerDistance(
  propertyAddress: string,
  employerAddress: string
): Promise<number> {
  try {
    const result = await calculateDistance(propertyAddress, employerAddress)
    return result.distance
  } catch (error) {
    console.error('Error calculating distance:', error)
    throw error
  }
}

/**
 * Check if distance exceeds threshold and requires explanation
 * @param distance - Distance in miles
 * @param threshold - Threshold in miles (default: 50)
 * @returns True if distance exceeds threshold
 */
export function requiresDistanceExplanation(
  distance: number,
  threshold: number = 50
): boolean {
  return distance > threshold
}



